import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const INTERACTIVE = "a,button,select,input,textarea,label,[role=button],[data-cursor]";

export const Cursor = () => {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const rx = useSpring(x, { stiffness: 220, damping: 24, mass: 0.6 });
  const ry = useSpring(y, { stiffness: 220, damping: 24, mass: 0.6 });
  const [state, setState] = useState({ hover: false, down: false, hidden: true, label: "" });
  const [enabled] = useState(() => window.matchMedia("(pointer: fine)").matches);

  useEffect(() => {
    if (!enabled) return;
    document.body.classList.add("has-cursor");
    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      const el = e.target.closest?.(INTERACTIVE);
      setState((s) => ({ ...s, hidden: false, hover: !!el, label: el?.dataset?.cursor || "" }));
    };
    const down = () => setState((s) => ({ ...s, down: true }));
    const up = () => setState((s) => ({ ...s, down: false }));
    const leave = () => setState((s) => ({ ...s, hidden: true }));
    const enter = () => setState((s) => ({ ...s, hidden: false }));
    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("mousedown", down);
    window.addEventListener("mouseup", up);
    document.documentElement.addEventListener("mouseleave", leave);
    document.documentElement.addEventListener("mouseenter", enter);
    return () => {
      document.body.classList.remove("has-cursor");
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mousedown", down);
      window.removeEventListener("mouseup", up);
      document.documentElement.removeEventListener("mouseleave", leave);
      document.documentElement.removeEventListener("mouseenter", enter);
    };
  }, [enabled, x, y]);

  if (!enabled) return null;
  const { hover, down, hidden, label } = state;
  const ring = label ? 96 : hover ? 72 : 36;

  return (
    <>
      {/* trailing ring */}
      <motion.div
        aria-hidden
        data-testid="cursor-ring"
        className="pointer-events-none fixed left-0 top-0 z-[200] flex items-center justify-center rounded-full border border-[#d4af37]"
        style={{ x: rx, y: ry, translateX: "-50%", translateY: "-50%", boxShadow: "0 0 24px rgba(212,175,55,0.25)" }}
        animate={{
          width: ring, height: ring,
          scale: down ? 0.85 : 1,
          opacity: hidden ? 0 : 1,
          backgroundColor: hover ? "rgba(212,175,55,0.14)" : "rgba(212,175,55,0)",
        }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
      >
        {label && (
          <motion.span
            initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }}
            className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#e8c979]"
          >
            {label}
          </motion.span>
        )}
      </motion.div>
      {/* precise dot */}
      <motion.div
        aria-hidden
        data-testid="cursor-dot"
        className="pointer-events-none fixed left-0 top-0 z-[201] rounded-full bg-[#e8c979]"
        style={{ x, y, translateX: "-50%", translateY: "-50%", boxShadow: "0 0 10px rgba(232,201,121,0.9)" }}
        animate={{ width: hover ? 4 : 7, height: hover ? 4 : 7, opacity: hidden || label ? 0 : 1, scale: down ? 1.6 : 1 }}
        transition={{ duration: 0.25 }}
      />
    </>
  );
};
